<template>
    <div class="muven-tip muven-tip-standalone tip-danger mb-3" ref="root" v-if="shouldShow">
        <fa icon="info" class="tip-icon" />

        <p v-if="variant === 'detalhe' && !diagnostic.isFalsePositiveOnVariations()">
            <strong>NÃO</strong> será possível atualizar este produto nos canais.
        </p>

        <p>{{ headline }}</p>
        <p>{{ nextStep }}</p>

        <button type="button" class="diagnostic-toggle" @click="toggle()">
            <i class="fas" :class="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
            {{ collapsed ? 'Ver mais detalhes' : 'Ocultar detalhes' }}
        </button>

        <div class="diagnostic-report" v-show="!collapsed">
            <div class="diagnostic-report-header">
                <span class="diagnostic-report-title">Diagnóstico de integração</span>
                <span class="diagnostic-report-ref">#{{ product.id }}<template v-if="product.skuCode"> · {{ product.skuCode }}</template></span>
            </div>

            <div class="diagnostic-report-body">
                <p class="diagnostic-section-label">Por que este aviso apareceu</p>

                <p class="diagnostic-trigger">{{ diagnostic.getTriggerExplanation() }}</p>

                <p class="diagnostic-section-label">Onde a integração parou</p>

                <ol class="diagnostic-chain">
                    <li v-for="step in diagnostic.getChain()" :key="step.step" class="diagnostic-chain-step"
                        :class="chainStepClass(step)">
                        <span class="diagnostic-chain-marker">
                            <i class="fas"
                                :class="step.isBroken ? 'fa-exclamation' : (step.isReached ? 'fa-check' : 'fa-minus')"></i>
                        </span>
                        <span class="diagnostic-chain-label">{{ step.label }}</span>
                        <span class="diagnostic-chain-hint">{{ step.hint }}</span>
                    </li>
                </ol>

                <template v-if="diagnostic.hasIssues()">
                    <p class="diagnostic-section-label">O que está faltando</p>

                    <div v-for="(issue, index) in diagnostic.getIssues()" :key="issue.code + index"
                        class="diagnostic-issue" :class="issueClass(issue)">
                        <p class="diagnostic-issue-title">
                            <span class="diagnostic-issue-badge">{{ issue.isBlocking ? 'Bloqueia' : 'Atenção' }}</span>
                            {{ issue.title }}
                        </p>
                        <p class="diagnostic-issue-detail">{{ issue.detail }}</p>
                        <p class="diagnostic-issue-affected" v-if="issue.affected.length">
                            <span v-for="item in issue.affected" :key="item" class="diagnostic-chip">{{ item }}</span>
                        </p>
                        <p class="diagnostic-issue-who">Quem resolve: <strong>{{ issue.whoFixes }}</strong></p>
                    </div>
                </template>

                <template v-if="diagnostic.getSuggestedActions().length">
                    <p class="diagnostic-section-label">O que você pode tentar agora</p>

                    <ol class="diagnostic-steps">
                        <li v-for="(action, index) in diagnostic.getSuggestedActions()" :key="index"
                            class="diagnostic-step" :class="{'step-warning': action.tone === 'warning'}">
                            <p class="diagnostic-step-title">{{ action.title }}</p>
                            <p class="diagnostic-step-detail">{{ action.description }}</p>
                            <div class="diagnostic-step-actions"
                                v-if="action.command || (action.links && action.links.length)">
                                <button v-if="action.command === 'force-sync'" type="button"
                                    class="btn btn-sm btn-outline-secondary" :disabled="forcingSync"
                                    @click="forceChannelsSync()">
                                    Forçar sincronização

                                    <Loading v-show="forcingSync" />
                                </button>

                                <a v-for="link in action.links" :key="link.route"
                                    :href="resolveHref(link.route)" target="_blank"
                                    rel="noopener" class="diagnostic-step-link">
                                    {{ link.label }}
                                    <fa icon="external-link-alt" class="muven-external-link ml-1" />
                                </a>
                            </div>
                        </li>
                    </ol>
                </template>

                <p class="diagnostic-section-label">Empresas e canais recebidos</p>

                <p class="diagnostic-empty" v-if="!diagnostic.hasStoreChannelMatrix()">
                    Nenhum vínculo com empresa chegou para este produto.
                </p>

                <div class="table-responsive" v-else>
                    <table class="table table-sm diagnostic-table">
                        <thead>
                            <tr>
                                <th>Empresa</th>
                                <th>Vínculo</th>
                                <th>Canal</th>
                                <th>Exportação do canal</th>
                                <th>Anúncio associado</th>
                                <th>Situação</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(store, storeIndex) in diagnostic.getStoreChannelMatrix()">
                                <tr v-if="!store.channels.length" :key="`store-${storeIndex}`">
                                    <td>
                                        {{ store.storeName }}
                                        <span class="diagnostic-flag" v-if="!store.storeIsActive">INATIVA</span>
                                        <span class="diagnostic-variation" v-if="store.variationName">{{ store.variationName }}</span>
                                    </td>
                                    <td class="diagnostic-mono">
                                        {{ store.idProductStore | infoOrPlaceholder }}
                                        <span class="diagnostic-flag" v-if="!store.productStoreIsActive">INATIVO</span>
                                    </td>
                                    <td colspan="4" class="diagnostic-absent">Nenhum canal vinculado</td>
                                </tr>

                                <tr v-for="(channel, channelIndex) in store.channels"
                                    :key="`store-${storeIndex}-channel-${channelIndex}`">
                                    <td>
                                        <template v-if="channelIndex === 0">
                                            {{ store.storeName }}
                                            <span class="diagnostic-flag" v-if="!store.storeIsActive">INATIVA</span>
                                            <span class="diagnostic-variation" v-if="store.variationName">{{ store.variationName }}</span>
                                        </template>
                                    </td>
                                    <td class="diagnostic-mono">
                                        <template v-if="channelIndex === 0">
                                            {{ store.idProductStore | infoOrPlaceholder }}
                                            <span class="diagnostic-flag" v-if="!store.productStoreIsActive">INATIVO</span>
                                        </template>
                                    </td>
                                    <td>{{ channel.name }}</td>
                                    <td>
                                        <span v-if="channel.allowExportDescription">
                                            {{ channel.allowExportDescription }}
                                        </span>
                                        <span v-else class="diagnostic-muted">não confirmada</span>
                                    </td>
                                    <td class="diagnostic-mono">
                                        <span v-if="channel.hasExternalChannelId">{{ channel.idExternalChannel }}</span>
                                        <span v-else class="diagnostic-absent">
                                            AUSENTE
                                            <span class="diagnostic-absent-hint" v-if="channel.exportsNewProducts">
                                                deveria ter sido criado pelo Muven
                                            </span>
                                            <span class="diagnostic-absent-hint"
                                                v-else-if="channel.exportsNewProducts === false">
                                                associação manual
                                            </span>
                                        </span>
                                    </td>
                                    <td>
                                        <span class="diagnostic-absent" v-if="channel.filteredByPriceStore">
                                            Descartado: canal gerencia preço pela loja {{ channel.priceManagementStoreId }}
                                        </span>
                                        <span class="diagnostic-absent" v-else-if="!channel.validInChannel">
                                            Marcado como inválido no canal
                                        </span>
                                        <span class="diagnostic-muted" v-else-if="!channel.isActive">Inativo</span>
                                        <span class="diagnostic-ok" v-else>Ok</span>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>

                <div class="diagnostic-actions">
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="copyDiagnostic()">
                        <fa :icon="['far', 'copy']" class="mr-1" />
                        Copiar diagnóstico
                    </button>

                    <a class="btn btn-sm btn-outline-secondary" v-if="auditUrl" :href="auditUrl" target="_blank">
                        Ir para auditoria
                        <fa icon="external-link-alt" class="muven-external-link ml-1" />
                    </a>
                </div>

                <p class="diagnostic-footnote">
                    O vínculo do produto com a <strong>empresa</strong> é criado pela retaguarda. Já o vínculo
                    com o <strong>canal</strong> e a associação com o anúncio são criados pelo Muven, na
                    exportação automática ou na associação manual. Ao abrir um chamado, cole o diagnóstico
                    acima: ele traz os identificadores técnicos que o suporte precisa.
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import '@fortawesome/fontawesome-free/css/all.css';
import productService from '@/product/services/ProductService'
import { ProductModel } from '@/product/models/ProductModel'
import { infoOrPlaceholder } from '@/utils/filters/InformationOrPlaceholderFilter'
import {
    ProductIntegrationDiagnosticModel,
    IntegrationChainStepEnum,
    IntegrationIssueOriginEnum,
} from '@/product/models/ProductIntegrationDiagnosticModel'

export default {
    name: 'ProductIntegrationDiagnosticCmp',
    filters: {
        infoOrPlaceholder,
    },
    props: {
        product: {
            type: Object,
            required: true,
        },
        backendName: {
            type: String,
            default: 'do seu ERP',
        },
        channelList: {
            type: Array,
            default: () => [],
        },
        isBackendShop: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        /**
         * Só a edição informa isto: é o mesmo !hasChannels() que bloqueia o
         * salvamento, para o painel e o bloqueio nunca divergirem.
         */
        blocked: {
            type: Boolean,
            default: false,
        },
        auditUrl: {
            type: String,
            default: '',
        },
        variant: {
            type: String,
            default: 'edicao',
        },
    },
    data() {
        return {
            collapsed: true,
            forcingSync: false,
        }
    },
    computed: {
        diagnostic() {
            const product = this.product instanceof ProductModel ? this.product : new ProductModel(this.product)

            return new ProductIntegrationDiagnosticModel(
                product, this.backendName, this.channelList, this.isBackendShop, this.auditUrl)
        },
        /**
         * A edição segue o que bloqueia o salvamento; o detalhamento, que não tem
         * formulário, mostra o painel sempre que há causa bloqueante. Antes ele
         * olhava só hasStores(), o primeiro elo da cadeia, e por isso deixava
         * passar qualquer falha do meio para a frente.
         */
        shouldShow() {
            if (this.loading) {
                return false
            }

            if (this.variant === 'edicao') {
                return this.blocked
            }

            return !!this.diagnostic.getBlockingIssues().length
        },
        /**
         * O texto de abertura acompanha a causa. Culpar a retaguarda sem
         * verificação era justamente o que confundia o suporte.
         */
        headline() {
            const step = this.diagnostic.getBrokenStep()

            if (!step) {
                return 'Este produto está sem canais disponíveis no formulário.'
            }

            switch (step.step) {
                case IntegrationChainStepEnum.ProductStore:
                    return 'Este produto ainda não foi vinculado a nenhuma empresa. ' +
                        'Esse vínculo é criado pela sua retaguarda, não pelo Muven.'
                case IntegrationChainStepEnum.ProductStoreChannel:
                    return 'Este produto está vinculado a uma empresa, mas não a nenhum canal de venda. ' +
                        'Esse vínculo é criado pelo Muven, não pela sua retaguarda.'
                case IntegrationChainStepEnum.Canal:
                    return 'Este produto ainda não foi associado ao anúncio correspondente em nenhum canal.'
                default:
                    return 'Este produto ainda não foi totalmente integrado com o Muven.'
            }
        },
        nextStep() {
            const step = this.diagnostic.getBrokenStep()

            if (this.diagnostic.isFalsePositiveOnVariations()) {
                return 'Você ainda consegue salvar as alterações: elas serão aplicadas às variações. ' +
                    'Veja os detalhes antes de abrir um chamado.'
            }

            // Tanto o vínculo com o canal quanto a associação com o anúncio são
            // criados pelo Muven, então nenhum dos dois casos leva ao ERP.
            const isChannelSide = step && (
                step.step === IntegrationChainStepEnum.Canal ||
                step.step === IntegrationChainStepEnum.ProductStoreChannel
            )

            if (isChannelSide) {
                // Associar à mão só faz sentido quando o anúncio já existe no
                // canal. Se o canal publica sozinho, mandar associar contradiz
                // o resto do painel.
                if (this.diagnostic.shouldMuvenResolveAutomatically()) {
                    return 'O canal está configurado para publicar novos produtos sozinho, então o Muven ' +
                        'deveria resolver isto por conta própria. Comece pela auditoria: é lá que aparece ' +
                        'a resposta do canal.'
                }

                // Mesmo aqui a auditoria vale: um vínculo sem anúncio associado
                // faz o agente tratar o produto como novo e tentar enviar, e o
                // resultado dessa tentativa fica registrado lá.
                return 'Comece pela auditoria: é lá que aparece a resposta do canal. ' +
                    'Se o anúncio já existir no canal, faça a associação em ' +
                    '"Canais → Associação de produtos".'
            }

            return `Aguarde alguns momentos e atualize a página. Se esta mensagem persistir, ` +
                `entre em contato com o suporte ${this.backendName}.`
        },
    },
    methods: {
        /**
         * Cópia no clique, e não via ClipboardJS ligado no mounted: o painel usa
         * v-if, então o botão não existe enquanto a tela carrega e o listener
         * nunca chegava a ser criado.
         */
        async copyDiagnostic() {
            const text = this.diagnostic.toSupportText()

            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(text)
                } else if (!this.copyWithFallback(text)) {
                    throw new Error('copy command failed')
                }

                this.$floatingAlert.info({
                    message: 'Diagnóstico copiado. Cole no chamado do suporte.',
                })
            } catch (e) {
                this.$floatingAlert.error({
                    message: 'Não foi possível copiar o diagnóstico. Selecione o texto do painel e copie manualmente.',
                })
            }
        },
        /** navigator.clipboard exige contexto seguro, que nem sempre existe em dev. */
        copyWithFallback(text) {
            const textarea = document.createElement('textarea')

            textarea.value = text
            textarea.setAttribute('readonly', '')
            textarea.style.position = 'fixed'
            textarea.style.opacity = '0'
            document.body.appendChild(textarea)
            textarea.select()

            let copied = false

            try {
                copied = document.execCommand('copy')
            } catch (e) {
                copied = false
            }

            document.body.removeChild(textarea)

            return copied
        },
        /**
         * As ações abrem em outra aba para o usuário não perder o que está
         * editando, então precisam de href absoluto em vez de router-link.
         */
        resolveHref(route) {
            return this.$router.resolve(route).href
        },
        /**
         * Mesma ação do botão "Forçar sincronização" da tela de detalhes, trazida
         * para dentro do painel para o usuário não precisar trocar de tela.
         */
        async forceChannelsSync() {
            this.forcingSync = true

            try {
                await productService.forceChannelsSync(this.product.id)
                this.$floatingAlert.info({
                    message: `Solicitação de sincronização do produto <b>${this.product.name}</b> registrada com sucesso.`,
                    timeVisible: 10000,
                })
                this.$emit('synced')
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message || `Houve um problema no momento de forçar a sincronização do produto <b>${this.product.name}</b>. Tente novamente mais tarde.`,
                })
            }

            this.forcingSync = false
        },
        toggle() {
            this.collapsed = !this.collapsed
        },
        /** Usado pelo formulário para explicar por que o salvamento foi barrado. */
        getBlockedSaveMessage() {
            return this.diagnostic.getBlockedSaveMessage()
        },
        expand() {
            this.collapsed = false

            this.$nextTick(() => {
                if (this.$refs.root && this.$refs.root.scrollIntoView) {
                    this.$refs.root.scrollIntoView({behavior: 'smooth', block: 'start'})
                }
            })
        },
        chainStepClass(step) {
            if (step.isBroken) {
                return 'chain-broken'
            }

            return step.isReached ? 'chain-reached' : 'chain-pending'
        },
        issueClass(issue) {
            switch (issue.origin) {
                case IntegrationIssueOriginEnum.Muven:
                    return 'issue-muven'
                case IntegrationIssueOriginEnum.Canal:
                    return 'issue-canal'
                default:
                    return 'issue-retaguarda'
            }
        },
    },
}
</script>

<style lang="scss" scoped>
$color-backend: var(--danger-solid);
$color-muven: var(--warning-solid);
$color-channel: var(--info-solid);
$color-ok: var(--success-solid);
$color-slate: var(--surface-tooltip);

.diagnostic-toggle {
    background: none;
    border: none;
    padding: 4px 0 0;
    color: var(--text-soft);
    font-size: .95em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .5px;

    &:hover {
        color: var(--text-strong);
    }

    i {
        font-size: .8em;
        margin-right: 4px;
    }
}

.diagnostic-report {
    margin-top: 12px;
    border: 1px solid var(--border);
    border-radius: 4px;
    background-color: var(--surface-1);
}

.diagnostic-report-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    padding: 8px 15px;
    background-color: $color-slate;
    border-radius: 3px 3px 0 0;
}

.diagnostic-report-title {
    color: var(--text-on-brand);
    font-size: .85em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1.5px;
}

.diagnostic-report-ref {
    color: var(--text-faint);
    font-family: monospace;
    font-size: .85em;
}

.diagnostic-report-body {
    padding: 15px;
}

.diagnostic-section-label {
    margin: 0 0 10px;
    font-size: .8em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-faint);

    &:not(:first-child) {
        margin-top: 22px;
    }
}

.diagnostic-chain {
    display: flex;
    flex-wrap: wrap;
    list-style: none;
    margin: 0;
    padding: 0;
}

.diagnostic-chain-step {
    position: relative;
    flex: 1 1 140px;
    min-width: 140px;
    padding: 0 10px 0 0;

    &:not(:last-child)::after {
        content: '';
        position: absolute;
        top: 11px;
        right: 4px;
        width: 6px;
        height: 6px;
        border-top: 2px solid var(--border);
        border-right: 2px solid var(--border);
        transform: rotate(45deg);
    }

    &.chain-reached .diagnostic-chain-marker {
        background-color: $color-ok;
    }

    &.chain-broken {
        .diagnostic-chain-marker {
            background-color: $color-backend;
        }

        .diagnostic-chain-label {
            color: $color-backend;
        }
    }

    &.chain-pending {
        .diagnostic-chain-marker {
            background-color: var(--border-strong);
        }

        .diagnostic-chain-label,
        .diagnostic-chain-hint {
            color: var(--text-faint);
        }
    }
}

.diagnostic-chain-marker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 100%;
    color: var(--text-on-brand);
    font-size: .65em;
}

.diagnostic-trigger {
    margin-bottom: 4px;
    padding: 10px 12px;
    background-color: var(--surface-2);
    border-radius: 3px;
    line-height: 1.5;
    color: var(--text) !important;
}

.diagnostic-chain-label {
    display: block;
    margin-top: 6px;
    font-size: .95em;
    font-weight: 700;
    color: var(--text);
}

.diagnostic-chain-hint {
    display: block;
    margin-top: 2px;
    font-size: .8em;
    line-height: 1.35;
    color: var(--text-muted);
}

.diagnostic-issue {
    padding: 10px 12px;
    margin-bottom: 8px;
    background-color: var(--surface-2);
    border-left: 3px solid $color-backend;

    &.issue-muven {
        border-left-color: $color-muven;
    }

    &.issue-canal {
        border-left-color: $color-channel;
    }
}

.diagnostic-issue-title {
    font-weight: 600;
    color: var(--text) !important;
}

.diagnostic-issue-badge {
    display: inline-block;
    margin-right: 6px;
    padding: 1px 6px;
    border-radius: 2px;
    background-color: var(--surface-3);
    color: var(--text-soft);
    font-size: .7em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .5px;
    vertical-align: middle;
}

.diagnostic-issue-detail {
    margin-top: 4px;
    line-height: 1.45;
}

.diagnostic-issue-affected {
    margin-top: 6px;
}

.diagnostic-chip {
    display: inline-block;
    margin: 2px 4px 2px 0;
    padding: 2px 7px;
    border: 1px solid var(--border);
    border-radius: 10px;
    background-color: var(--surface-1);
    font-size: .8em;
    color: var(--text-soft);
}

.diagnostic-issue-who {
    margin-top: 6px;
    font-size: .85em;
}

.diagnostic-steps {
    margin: 0;
    padding: 0;
    list-style: none;
    counter-reset: diagnostic-step;
}

.diagnostic-step {
    position: relative;
    padding: 0 0 14px 34px;
    counter-increment: diagnostic-step;

    &::before {
        content: counter(diagnostic-step);
        position: absolute;
        left: 0;
        top: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 100%;
        background-color: $color-slate;
        color: var(--text-on-brand);
        font-size: .8em;
        font-weight: 700;
    }

    &:not(:last-child)::after {
        content: '';
        position: absolute;
        left: 11px;
        top: 28px;
        bottom: 4px;
        width: 2px;
        background-color: var(--border);
    }

    &.step-warning::before {
        content: '!';
        background-color: $color-muven;
    }
}

.diagnostic-step-title {
    font-weight: 600;
    color: var(--text) !important;
    padding-top: 2px;
}

.diagnostic-step-detail {
    margin-top: 2px;
    line-height: 1.45;
}

.diagnostic-step-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
}

.diagnostic-step-link {
    font-size: .9em;
    font-weight: 600;
}

.diagnostic-table {
    font-size: .85em;

    th {
        border-top: none;
        font-size: .9em;
        text-transform: uppercase;
        letter-spacing: .5px;
        color: var(--text-faint);
        font-weight: 700;
    }

    td {
        vertical-align: middle;
    }
}

.diagnostic-mono {
    font-family: monospace;
}

.diagnostic-absent {
    color: $color-backend;
    font-weight: 600;
}

.diagnostic-absent-hint {
    display: block;
    font-family: system-ui, sans-serif;
    font-size: .9em;
    font-weight: 400;
    color: var(--text-muted);
}

.diagnostic-ok {
    color: $color-ok;
}

.diagnostic-muted {
    color: var(--text-faint);
}

.diagnostic-flag {
    display: inline-block;
    margin-left: 4px;
    padding: 0 5px;
    border-radius: 2px;
    background-color: $color-backend;
    color: var(--text-on-brand);
    font-size: .7em;
    font-weight: 700;
    letter-spacing: .5px;
}

.diagnostic-variation {
    display: block;
    font-size: .85em;
    color: var(--text-muted);
}

.diagnostic-empty {
    font-style: italic;
}

.diagnostic-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 18px;
}

.diagnostic-footnote {
    margin-top: 12px;
    font-size: .85em;
    color: var(--text-muted) !important;
    line-height: 1.45;
}

code {
    font-size: .95em;
    color: var(--text-soft);
    background-color: var(--surface-3);
    padding: 0 3px;
    border-radius: 2px;
}
</style>
